/**
 * Analytics Page
 * v4.05 - 2026-04-26 - [185]: Staleness alert + Refresh Now button for KPI data
 * v3.38 - 2026-02-23 - Story 8: Replace react-sparklines with Plotly mini charts (ADR-002)
 * v3.28 - 2026-02-22 - Make trend chart cards clickable: navigate to related report
 * v3.21 - 2026-02-18 - Full rewrite: KPI cards + trend charts + AI Insights (was placeholder stub)
 *
 * Displays KPI card grid, expanded trend charts from sparkline data,
 * and AI-powered insights panel. Reuses existing components.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardActionArea, Skeleton, Alert, Button, CircularProgress, useTheme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Plot } from '../utils/plotlySetup';
import TopBar from '../components/TopBar';
import KPICardGrid from '../components/KPICardGrid';
import InsightsPanel from '../components/InsightsPanel';
import {
  dashboardKPIService,
  DashboardKPIsResponse,
  DashboardCardsResponse,
  KPIValue,
  KPICardDefinition,
} from '../services/api';
import { useClerkAuth } from '../App';

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 hours
const PIPELINE_BUSY_STATES = new Set(['pipeline_running', 'awaiting_approval']);

// v3.21 - Format value for chart labels (reuses logic from KPICardGrid)
const formatChartValue = (
  value: number | null,
  format: string,
  formatOptions?: { currency?: string; decimals?: number; abbreviate?: boolean }
): string => {
  if (value === null) return '--';
  const decimals = formatOptions?.decimals ?? 0;
  if (format === 'currency') {
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(decimals)}`;
  }
  if (format === 'percentage') return `${value.toFixed(decimals)}%`;
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(decimals);
};

// v3.28 - Individual trend chart card (clickable -> navigates to Reports)
// v3.38 - Replaced react-sparklines with Plotly mini line chart
interface TrendChartCardProps {
  card: KPICardDefinition;
  kpiValue: KPIValue;
  onClick?: () => void;
}

const TrendChartCard: React.FC<TrendChartCardProps> = ({ card, kpiValue, onClick }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const isTrendGood = () => {
    // v3.95 - UX-KPI-02: suppress trend chip when card has no value
    // == null catches both null and undefined; 0.0 == null is false so
    // zero-value cards are unaffected
    if (kpiValue?.currentValue == null) return null;
    if (!kpiValue.changeDirection || kpiValue.changeDirection === 'flat') return null;
    const isUp = kpiValue.changeDirection === 'up';
    return isUp === (card.trendDirection === 'up_is_good');
  };

  const trendGood = isTrendGood();
  const trendColor = trendGood === null ? theme.palette.text.secondary :
    trendGood ? theme.palette.success.main : theme.palette.error.main;

  const getTrendIcon = () => {
    if (!kpiValue.changeDirection) return null;
    switch (kpiValue.changeDirection) {
      case 'up': return <TrendingUpIcon fontSize="small" />;
      case 'down': return <TrendingDownIcon fontSize="small" />;
      default: return <TrendingFlatIcon fontSize="small" />;
    }
  };

  return (
    <Card sx={{ height: '100%', borderLeft: `4px solid ${card.color}`, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 4 } : {} }}>
      <CardActionArea onClick={onClick} disabled={!onClick}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {card.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', color: trendColor }}>
            {getTrendIcon()}
            {kpiValue.changePercent !== null && kpiValue.changePercent !== undefined && (
              <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 600 }}>
                {kpiValue.changePercent > 0 ? '+' : ''}
                {kpiValue.changePercent.toFixed(1)}%
              </Typography>
            )}
          </Box>
        </Box>

        {/* Current value */}
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          {formatChartValue(kpiValue.currentValue, card.format, card.formatOptions)}
        </Typography>

        {/* v3.38 - Plotly mini line chart (replaces react-sparklines) */}
        {kpiValue.sparkline && kpiValue.sparkline.length > 1 && (
          <Box sx={{ mb: 1 }}>
            <Plot
              data={[{
                x: kpiValue.sparklinePeriods || kpiValue.sparkline.map((_, i) => i),
                y: kpiValue.sparkline,
                type: 'scatter',
                mode: 'lines',
                line: { color: card.color, width: 2.5, shape: 'spline' },
                hoverinfo: 'x+y' as any,
              }]}
              layout={{
                height: 80,
                margin: { l: 0, r: 0, t: 0, b: 0 },
                xaxis: { visible: false },
                yaxis: { visible: false },
                paper_bgcolor: 'transparent',
                plot_bgcolor: 'transparent',
                showlegend: false,
                font: { color: dark ? '#E0E0E0' : '#212121' },
              }}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%', height: 80 }}
            />

            {/* Period labels */}
            {kpiValue.sparklinePeriods && kpiValue.sparklinePeriods.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 0.5 }}>
                {kpiValue.sparklinePeriods.map((period, idx) => (
                  <Typography key={idx} variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {period}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {card.description}
          </Typography>
          {kpiValue.asOfDate && (
            <Typography variant="caption" color="text.secondary">
              {new Date(kpiValue.asOfDate).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      </CardContent>
      </CardActionArea>
    </Card>
  );
};

// v3.21 - Loading skeleton for trend charts
const TrendChartsSkeleton: React.FC = () => (
  <Grid container spacing={3}>
    {[1, 2, 3, 4].map((i) => (
      <Grid item xs={12} md={6} key={i}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="30%" height={36} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={80} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const Analytics: React.FC = () => {
  const navigate = useNavigate(); // v3.28
  const { workflowState, tenantId } = useClerkAuth(); // v4.05 - [185]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpisResponse, setKpisResponse] = useState<DashboardKPIsResponse | null>(null);
  const [cardsResponse, setCardsResponse] = useState<DashboardCardsResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false); // v4.05 - [185]
  const [refreshBanner, setRefreshBanner] = useState<'success' | 'error' | null>(null); // v4.05 - [185]
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // v3.21 - Fetch KPI data for trend charts
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpis, cards] = await Promise.all([
        dashboardKPIService.getKPIs(),
        dashboardKPIService.getCards(),
      ]);
      setKpisResponse(kpis);
      setCardsResponse(cards);
    } catch (err: any) {
      console.error('Failed to fetch analytics data:', err);
      setError(err.userMessage || err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // v4.05 - [185]: Trigger gold-only pipeline refresh, show banner, auto-dismiss after 10s
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshBanner(null);
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    try {
      await dashboardKPIService.dashboardRefresh(tenantId!);
      setRefreshBanner('success');
    } catch {
      setRefreshBanner('error');
    } finally {
      setRefreshing(false);
      bannerTimerRef.current = setTimeout(() => setRefreshBanner(null), 10000);
    }
  }, [tenantId]);

  useEffect(() => {
    return () => { if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current); };
  }, []);

  // v4.05 - [185]: Determine staleness — >24h or no computedAt
  const isStale = !kpisResponse?.computedAt ||
    (Date.now() - new Date(kpisResponse.computedAt).getTime() > STALE_THRESHOLD_MS);
  const pipelineBusy = PIPELINE_BUSY_STATES.has(workflowState || '');

  // Build trend chart data: cards that have sparkline data
  const trendCharts: Array<{ card: KPICardDefinition; kpiValue: KPIValue }> = [];
  if (kpisResponse && cardsResponse) {
    const kpis = kpisResponse.kpis || {};
    const cards = cardsResponse.cards || [];
    const sortedCards = [...cards].sort((a, b) => a.position - b.position);

    for (const card of sortedCards) {
      const kpiValue = Object.values(kpis).find((kpi) => kpi.cardId === card.cardId);
      if (kpiValue && kpiValue.sparkline && kpiValue.sparkline.length > 1) {
        trendCharts.push({ card, kpiValue });
      }
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <TopBar title="Analytics" subtitle="Advanced metrics and trends" />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* v4.05 - [185]: Staleness alert with Refresh Now button */}
        {!loading && isStale && !pipelineBusy && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing…' : 'Refresh Now'}
              </Button>
            }
          >
            Your KPI data is more than 24 hours old.
          </Alert>
        )}

        {/* v4.05 - [185]: Post-refresh feedback banner */}
        {refreshBanner === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Refresh started — updated data will appear within a few minutes.
          </Alert>
        )}
        {refreshBanner === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not start refresh. Please try again later.
          </Alert>
        )}

        {/* v3.21 - KPI Card Grid (same component as Dashboard home) */}
        <KPICardGrid />

        {/* v3.21 - Trend Charts section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Trends
          </Typography>

          {loading ? (
            <TrendChartsSkeleton />
          ) : trendCharts.length > 0 ? (
            <Grid container spacing={3}>
              {/* v3.28 - Clickable trend cards: navigate to Reports page */}
              {trendCharts.map(({ card, kpiValue }) => (
                <Grid item xs={12} md={6} key={card.cardId}>
                  <TrendChartCard card={card} kpiValue={kpiValue} onClick={() => navigate('/reports')} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              Trend data will appear here after your data pipeline has processed multiple periods.
            </Alert>
          )}
        </Box>

        {/* v3.21 - AI Insights panel (same component as Dashboard home) */}
        <Box sx={{ mt: 4 }}>
          <InsightsPanel />
        </Box>
      </Container>
    </Box>
  );
};

export default Analytics;
